import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import {
  ChevronDown,
  MessageCircle,
  Settings,
  LogOut,
  Crown,
  Award,
  UserPlus,
  User,
  Disc3,
  Home,
  Users,
  UsersRound,
  Trophy,
  LayoutTemplate,
  BarChart3,
  Plus,
  Heart,
  Tag,
  Compass,
  CalendarDays,
  CircleDotDashed,
} from "lucide-react";
import { cn } from "@/lib/utils";
import vinyIcon from "@/assets/viny-icon-2026.png.asset.json";

import { openProDrawer } from "@/components/ProDrawer";
import { FramedAvatar } from "@/components/FramedAvatar";
import { NotificationBell } from "@/components/NotificationBell";
import { PremiumBadge } from "@/components/PremiumBadge";

type Profile = {
  id: string;
  username: string;
  display_name?: string | null;
  avatar_url?: string | null;
};

type NavLink = {
  to: string;
  label: string;
  icon: typeof Home;
  search?: Record<string, string>;
  /** Rota exata (sem prefixo) para marcar como ativo */
  exact?: boolean;
  /** Exibe a tag "novo" ao lado do label. */
  isNew?: boolean;
};

type NavGroup = { id: string; label: string; items: readonly NavLink[] };

/** Painel completo, aberto pela pílula da logo (padrão Framer). */
const groups: readonly NavGroup[] = [
  {
    id: "colecao",
    label: "Coleção",
    items: [
      { to: "/explore", label: "Explorar", icon: Compass },
      { to: "/collection", label: "Minha coleção", icon: Disc3, exact: true },
      { to: "/collection", label: "Meus interesses", icon: Heart, search: { tab: "wishlist" } },
      { to: "/collection", label: "Meus anúncios", icon: Tag, search: { tab: "listed" } },
      { to: "/insights", label: "Insights", icon: BarChart3 },
      { to: "/mini-site", label: "Mini Site", icon: LayoutTemplate },
      { to: "/ritual", label: "Viny Ritual", icon: CircleDotDashed, isNew: true },
    ],
  },
  {
    id: "comunidade",
    label: "Comunidade",
    items: [
      { to: "/colecionadores", label: "Colecionadores", icon: Trophy },
      { to: "/marketplace", label: "Marketplace", icon: Tag },
      { to: "/comunidades", label: "Comunidades", icon: UsersRound },
    ],
  },
] as const;

type Tab = { to: string; label: string; search?: Record<string, string>; exact?: boolean };

/** Abas internas da seção atual — exibidas no centro da barra. */
function sectionFor(
  pathname: string,
  username?: string | null,
): { label: string; tabs: readonly Tab[] } {
  if (pathname.startsWith("/explore") || pathname.startsWith("/search"))
    return {
      label: "Explorar",
      tabs: [
        { to: "/explore", label: "Discos recentes", exact: true },
        { to: "/explore", label: "Feed", search: { tab: "feed" } },

      ],
    };
  // Meu próprio perfil vive em "Conta"; perfis de outras pessoas, em "Comunidade".
  const ownProfile = username ? pathname === `/profile/${username}` : false;
  if (
    pathname.startsWith("/marketplace") ||
    (pathname.startsWith("/profile") && !ownProfile) ||
    pathname.startsWith("/colecionadores") ||
    pathname.startsWith("/comunidades") ||
    pathname.startsWith("/chat")
  )
    return {
      label: "Comunidade",
      tabs: [
        { to: "/comunidades", label: "Comunidades" },
        { to: "/colecionadores", label: "Colecionadores" },
        { to: "/marketplace", label: "Marketplace" },
      ],
    };
  if (
    pathname.startsWith("/conquistas") ||
    pathname.startsWith("/levels") ||
    pathname.startsWith("/streaks") ||
    pathname.startsWith("/convidar") ||
    pathname.startsWith("/settings") ||
    ownProfile
  )
    return {
      label: "Conta",
      tabs: [
        ...(username
          ? [{ to: `/profile/${username}`, label: "Meu perfil", exact: true } as Tab]
          : []),
        { to: "/settings/profile", label: "Configurações" },
        { to: "/settings/performance", label: "Desempenho" },
      ],
    };
  return {
    label: "Coleção",
    tabs: [
      { to: "/collection", label: "Minha coleção", exact: true },
      { to: "/collection", label: "Interesses", search: { tab: "wishlist" } },
      { to: "/collection", label: "Vendas", search: { tab: "listed" } },
    ],
  };
}

export function DesktopTopBar({
  pathname,
  profile,
  isAuthenticated,
  authLoading,
  isPremium,
  hasUnread,
  onSignOut,
}: {
  pathname: string;
  profile: Profile | null | undefined;
  isAuthenticated: boolean;
  authLoading: boolean;
  isPremium: boolean;
  hasUnread: boolean;
  onSignOut: () => void;
}) {
  const navigate = useNavigate();
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [accountOpen, setAccountOpen] = useState(false);
  const navRef = useRef<HTMLElement | null>(null);
  const accountRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setOpenGroup(null);
    setAccountOpen(false);
  }, [pathname]);

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (!navRef.current?.contains(e.target as Node)) setOpenGroup(null);
      if (!accountRef.current?.contains(e.target as Node)) setAccountOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpenGroup(null);
        setAccountOpen(false);
      }
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const displayName = profile?.display_name || profile?.username || "Visitante";
  const section = sectionFor(pathname, profile?.username);
  const panelOpen = openGroup === "panel";
  // Lê da rota (não de window) para não quebrar a hidratação no SSR.
  const currentSearch = useRouterState({
    select: (st) => st.location.searchStr ?? "",
  });

  function tabActive(tab: Tab) {
    if (!pathname.startsWith(tab.to) && pathname !== tab.to) return false;
    if (tab.search) {
      const [k, v] = Object.entries(tab.search)[0]!;
      return currentSearch.includes(`${k}=${v}`);
    }
    if (tab.exact) return pathname === tab.to && !currentSearch.includes("tab=");
    return pathname.startsWith(tab.to);
  }

  return (
    <header className="fixed inset-x-0 top-0 z-50 hidden h-[60px] border-b border-white/[0.07] bg-[#0b0b0b]/95 backdrop-blur-xl lg:block">
      <div className="mx-auto flex h-full max-w-[1400px] items-center gap-4 px-5">
        {/* Pílula da logo com o menu completo */}
        <div className="relative shrink-0" ref={navRef as never}>
          <button
            type="button"
            onClick={() => setOpenGroup(panelOpen ? null : "panel")}
            aria-expanded={panelOpen}
            aria-label="Abrir navegação"
            className={cn(
              "flex h-9 items-center gap-2 rounded-xl border border-white/[0.07] bg-[#171717] pl-2.5 pr-2 text-[13.5px] font-semibold text-foreground transition hover:bg-[#1f1f1f]",
              panelOpen && "bg-[#1f1f1f]",
            )}
          >
            <img
              src={vinyIcon.url}
              alt="Viny"
              width={18}
              height={18}
              className="h-[18px] w-[18px] object-contain"
            />
            <span className="max-w-[140px] truncate">{section.label}</span>
            <ChevronDown
              className={cn(
                "h-3.5 w-3.5 text-muted-foreground transition-transform",
                panelOpen && "rotate-180",
              )}
            />
          </button>

          {panelOpen && (
            <nav
              aria-label="Navegação principal"
              className="absolute left-0 top-[calc(100%+6px)] w-[268px] overflow-hidden rounded-2xl border border-white/[0.07] bg-[#161616] p-1.5 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.95)]"
            >
              {groups.map((group) => (
                <div key={group.id} className="pb-1">
                  <div className="px-2.5 pb-1 pt-2 text-[12.5px] text-muted-foreground">
                    {group.label}
                  </div>
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const itemActive = item.search
                      ? false
                      : item.exact
                        ? pathname === item.to
                        : pathname.startsWith(item.to);
                    return (
                      <button
                        key={`${item.to}-${item.label}`}
                        type="button"
                        onClick={() => {
                          setOpenGroup(null);
                          navigate({
                            to: item.to,
                            ...(item.search ? { search: item.search as never } : {}),
                          } as never);
                        }}
                        className={cn(
                          "flex h-9 w-full items-center gap-3 rounded-lg px-2.5 text-left text-[13.5px] transition-colors",
                          itemActive
                            ? "bg-white/[0.08] text-foreground"
                            : "text-foreground/85 hover:bg-white/[0.05]",
                        )}
                      >
                        <Icon
                          className="h-4 w-4 shrink-0 text-muted-foreground"
                          strokeWidth={1.8}
                        />
                        <span className="truncate">{item.label}</span>
                        {item.isNew && (
                          <span className="ml-auto inline-flex rounded-full bg-emerald-400 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-black">
                            novo
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </nav>
          )}
        </div>

        {/* Abas internas da seção atual */}
        <nav className="mx-auto flex items-center gap-1" aria-label={`Abas de ${section.label}`}>
          {section.tabs.map((tab) => {
            const active = tabActive(tab);
            return (
              <button
                key={`${tab.to}-${tab.label}`}
                type="button"
                onClick={() =>
                  navigate({
                    to: tab.to,
                    ...(tab.search ? { search: tab.search as never } : {}),
                  } as never)
                }
                className={cn(
                  "rounded-lg px-3 py-1.5 text-[14px] transition-colors",
                  active
                    ? "bg-white/[0.1] font-semibold text-foreground shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]"
                    : "font-medium text-muted-foreground hover:bg-white/[0.04] hover:text-foreground",
                )}
              >
                {tab.label}
              </button>
            );
          })}
        </nav>

        {/* Ação + conta */}
        <div className="ml-auto flex shrink-0 items-center gap-2.5">
          {isAuthenticated && profile ? (
            <>
              <Link
                to="/chat"
                aria-label="Mensagens"
                className="relative grid h-9 w-9 place-items-center rounded-xl border border-white/[0.07] bg-[#171717] text-muted-foreground transition hover:text-foreground"
              >
                <MessageCircle className="h-[17px] w-[17px]" />
                {hasUnread && (
                  <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-[color:var(--action)]" />
                )}
              </Link>
              <NotificationBell />

              {!isPremium ? (
                <button
                  type="button"
                  onClick={openProDrawer}
                  className="inline-flex h-9 items-center gap-1.5 rounded-xl bg-[#7c3aed] px-3.5 text-[13.5px] font-semibold text-white transition hover:brightness-110"
                >
                  <Crown className="h-4 w-4" /> Seja Pro
                </button>
              ) : null}

              <div className="relative" ref={accountRef}>
                <button
                  type="button"
                  onClick={() => setAccountOpen((v) => !v)}
                  className="grid h-9 w-9 place-items-center"
                  aria-label="Conta"
                >
                  <FramedAvatar
                    userId={profile.id}
                    src={profile.avatar_url}
                    username={profile.username}
                    isPremium={isPremium}
                    size={32}
                  />
                </button>
                {accountOpen && (
                  <div className="absolute right-0 top-[calc(100%+6px)] max-h-[min(70vh,560px)] w-[260px] overflow-y-auto overscroll-contain rounded-2xl border border-white/[0.07] bg-[#161616] p-2 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.95)]">
                    <div className="flex items-center justify-between gap-2 px-2.5 py-2">
                      <div className="min-w-0">
                        <div className="truncate text-[13.5px] font-semibold">{displayName}</div>
                        <div className="truncate text-[12px] text-muted-foreground">
                          @{profile.username}
                        </div>
                      </div>
                      <PremiumBadge isPremium={isPremium} size="xs" />
                    </div>
                    <div className="my-1 border-t border-white/[0.07]" />
                    <Link
                      to="/profile/$username"
                      params={{ username: profile.username }}
                      className="flex h-9 items-center gap-2.5 rounded-lg px-2.5 text-[13.5px] text-foreground/85 hover:bg-white/[0.05]"
                    >
                      <User className="h-4 w-4 text-muted-foreground" /> Meu perfil
                    </Link>
                    <Link
                      to="/semanal"
                      className="flex h-9 items-center justify-between rounded-lg px-2.5 text-[13.5px] text-foreground/85 hover:bg-white/[0.05]"
                    >
                      <span className="flex items-center gap-2.5">
                        <CalendarDays className="h-4 w-4 text-primary" /> Viny Semanal
                      </span>
                      <span className="rounded-full bg-primary/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary">
                        novo
                      </span>
                    </Link>
                    <Link
                      to="/conquistas"
                      className="flex h-9 items-center gap-2.5 rounded-lg px-2.5 text-[13.5px] text-foreground/85 hover:bg-white/[0.05]"
                    >
                      <Award className="h-4 w-4 text-muted-foreground" /> Conquistas
                    </Link>
                    <Link
                      to="/convidar"
                      className="flex h-9 items-center gap-2.5 rounded-lg px-2.5 text-[13.5px] text-foreground/85 hover:bg-white/[0.05]"
                    >
                      <UserPlus className="h-4 w-4 text-muted-foreground" /> Convidar amigos
                    </Link>
                    <Link
                      to="/settings/profile"
                      className="flex h-9 items-center gap-2.5 rounded-lg px-2.5 text-[13.5px] text-foreground/85 hover:bg-white/[0.05]"
                    >
                      <Settings className="h-4 w-4 text-muted-foreground" /> Configurações
                    </Link>
                    <div className="my-1 border-t border-white/[0.07]" />
                    <button
                      type="button"
                      onClick={onSignOut}
                      className="flex h-9 w-full items-center gap-2.5 rounded-lg px-2.5 text-left text-[13.5px] text-destructive hover:bg-white/[0.05]"
                    >
                      <LogOut className="h-4 w-4" /> Sair
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : authLoading ? (
            <div className="h-9 w-9 animate-pulse rounded-xl bg-[#171717]" />
          ) : (
            <Link
              to="/auth"
              className="inline-flex h-9 items-center rounded-xl bg-[color:var(--action)] px-4 text-[13.5px] font-semibold text-white transition hover:brightness-110"
            >
              Entrar
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
